//Built-in libraries
//Third party libraries
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { getProjectTdmAnalytics } from '@/api/analytics';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import { usePermission } from '@/hooks/usePermission';
import { notifyUser } from '@/store/Toast';

//Custom components

//Services

import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import Checkbox from '@/components/UI/Form/Checkbox';
//Assets and Miscellaneous
import { convertEnumToText, getInitials } from '@/utils';
import { getTdmCompleteOverCount } from '@/utils/analytics';
import Styles from '../TableViewProjects.module.css';

//Component Begin
export default function ProjectRow({
  project,
  isLoading,
  handleProjectSelected,
  selectedProjects,
}: {
  project: IGetProjectsServiceResData;
  isLoading: boolean;
  handleProjectSelected: (projectId: number, selected: boolean) => void;
  selectedProjects: number[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  //Tanstack: version 5 query implementation
  const fetchProjectTdmAnalytics = useQuery({ queryKey: ['project-tdm-analytics'], queryFn: () => getProjectTdmAnalytics(project.id) });

  const projectTdmAnalytics = fetchProjectTdmAnalytics?.data;
  const tasksStat = getTdmCompleteOverCount(projectTdmAnalytics?.tasks);
  const deliverablesStat = getTdmCompleteOverCount(projectTdmAnalytics?.deliverables);
  const milestonesStat = getTdmCompleteOverCount(projectTdmAnalytics?.milestones);

  const projectDetailViewHandler = () => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname}/${project.id}`, { state: location });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  return (
    <>
      {isLoading &&
        [1, 2, 3, 4, 5].map((num) => (
          <tr key={num} className='animate-pulse'>
            <td className='px-6 py-4 whitespace-nowrap w-full'>
              <div className='flex items-center'>
                <span className='inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500'>
                  <span className='font-medium leading-none text-green-500 font-bold'></span>
                </span>
                <div className='ml-4 h-2 w-1/3'>
                  <div className='h-1 w-full rounded-md bg-gray-500 mb-2'></div>
                  <div className='h-1 w-1/2 rounded-md bg-gray-500'></div>
                </div>
              </div>
            </td>
            <td className='px-6 py-4 whitespace-nowrap w-full'>
              <div className='h-1 w-1/3 rounded-md bg-gray-500 mb-2'></div>
              <div className='h-1 w-1/2 rounded-md bg-gray-500'></div>
            </td>
            <td className='whitespace-nowrap w-full text-center'>
              <span className='h-4 w-1/2 inline-flex rounded-full bg-gray-500'></span>
            </td>
            <td className='px-6 py-4 whitespace-nowrap w-full'>
              <div className='h-2 rounded-md bg-gray-500 mb-2'></div>
            </td>
            <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
              <span className='block h-1 w-2 rounded-md bg-gray-500 mb-2'></span>
            </td>
          </tr>
        ))}
      {!isLoading && (
        <tr key={project.id} className='hover:bg-green-50 cursor-pointer' onClick={projectDetailViewHandler}>
          <td className='pl-4'>
            <Checkbox
              name='departmentLedProject'
              id='department-led-project'
              checked={selectedProjects.includes(project.id)}
              onChange={(e) => {
                handleProjectSelected(project.id, e.target.checked);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </td>
          <td className='px-6 py-4'>
            <div className='text-sm text-gray-900 flex flex-col w-72 lg:w-full'>
              <span className='break-all'>{project.name}</span>
              <span className='text-xs text-neutralColor-normal'>{project.fullAddress}</span>
            </div>
          </td>
          <td className='px-6 py-4 whitespace-nowrap capitalize'>
            <Badge status={project.stage ? project.stage : 'neutral'} text={convertEnumToText(project.status)} />
          </td>
          <td
            className={`text-center px-6 py-4 whitespace-nowrap 
          ${tasksStat.percentComplete < 100 && Styles.healthy}
          ${tasksStat.percentComplete < 50 && Styles.warning}
          ${tasksStat.percentComplete < 20 && Styles.danger}
          `}
          >
            {tasksStat.completeCount}/{tasksStat.totalCount}
          </td>
          <td
            className={`text-center px-6 py-4 whitespace-nowrap
          ${deliverablesStat.percentComplete < 100 && Styles.healthy}
          ${deliverablesStat.percentComplete < 50 && Styles.warning}
          ${deliverablesStat.percentComplete < 20 && Styles.danger}
          `}
          >
            {deliverablesStat.completeCount}/{deliverablesStat.totalCount}
          </td>
          <td
            className={`text-center px-6 py-4 whitespace-nowrap ${milestonesStat.percentComplete < 100 && Styles.healthy}
            ${milestonesStat.percentComplete < 50 && Styles.warning}
            ${milestonesStat.percentComplete < 20 && Styles.danger}`}
          >
            {milestonesStat.completeCount}/{milestonesStat.totalCount}
          </td>
          <td className='px-6 py-4 text-center'>
            <div className='flex gap-2 items-center justify-start w-40'>
              <ImageCircularAvatar
                imageUrl={project.projectLeader.profileImageUrl}
                fallbackText={getInitials(project.projectLeader.name)}
              />
              <div className='text-sm font-medium text-gray-900 truncate'>{project.projectLeader.name}</div>
            </div>
          </td>
          {/* <td className="px-6 py-4 whitespace-nowrap">
            <div className="w-full bg-gray-200 rounded-full h-5">
              <div className="w-1/2 bg-green-500 h-full rounded-full px-2 py-1 text-white flex items-center text-xs font-bold">
                25%
              </div>
            </div>
          </td> */}
        </tr>
      )}
    </>
  );
}
